if (!customElements.get("m-product-tabs")) {
  class MProductTabs extends HTMLElement {
    constructor() {
      super();
      this.selectors = {
        loadMoreBtn: "[data-load-more-product]",
        productsContainer: "[data-products-container]",
        tabContent: [".m-tab-content"],
      };
      this.tabSliders = [];
      this.sliderEnabled = this.dataset.enableSlider === "true";
      this.mobileSliderDisable = this.dataset.mobileDisableSlider === "true";
      this.buttonType = this.dataset.buttonType;
      this.domNodes = queryDomNodes(this.selectors, this);
      this.showPagination = this.dataset.showPagination === "true";
      this.showNavigation = this.dataset.showNavigation === "true";
      this.items = this.dataset.items;
    }

    connectedCallback() {
      // In the theme editor everything must be live immediately, since
      // shopify:block:select can fire before the section is scrolled into view.
      if (window.Shopify && Shopify.designMode) {
        this.init();
        // Non-active tabs ship an empty shell (see product-tabs.liquid) and are
        // normally only fetched on click - the editor can't wait for that.
        this.domNodes.tabContent.forEach((_, index) => this.loadTabContent(index));
        return;
      }
      MinimogTheme.initWhenVisible({
        element: this,
        threshold: 400,
        callback: () => this.init(),
      });
    }

    init() {
      this.startedSliders = [];
      if (this.sliderEnabled) {
        // Only the active tab gets a Swiper on load. The other tabs are
        // display:none, so building them up front costs one Swiper instance plus
        // a forced layout read each, for markup nobody can see yet.
        this.initSliderFor(0);
        const reinit = () => this.startedSliders.forEach((block) => this.initSliderByScreenSize(block));
        document.addEventListener("matchMobile", reinit);
        document.addEventListener("unmatchMobile", reinit);
      }
      if (!this.sliderEnabled && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;

        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
        this.canLoad = true;
        this.currentPage = 1;
        for (let block of this.domNodes.tabContent) {
          this.initLoadMore(block);
        }
      }
      document.addEventListener("matchMobile", () => {
        if (MinimogTheme.config.mqlMobile && this.mobileSliderDisable && this.buttonType === "load") {
          this.canLoad = true;
          this.currentPage = 1;
          for (let block of this.domNodes.tabContent) {
            this.initLoadMore(block);
          }
        }
      });
      this.initTabs();
      this.initMobileSelect();
      this.prefetchOtherTabs();

      if (Shopify.designMode) {
        document.addEventListener("shopify:block:select", (event) => {
          const blockSelectedIsTab = event.target.classList.contains("m-tab-content");
          if (!blockSelectedIsTab) return;
          const dataIndex = event.target && event.target.dataset.index;
          this.tabs.setActiveTab(dataIndex);
        });
      }
    }

    initSliderFor(index) {
      if (!this.sliderEnabled) return;
      const block = this.domNodes.tabContent[index];
      if (!block || this.startedSliders.includes(block)) return;
      this.startedSliders.push(block);
      this.initSliderByScreenSize(block);
    }

    initTabs() {
      this.tabs = new MinimogTheme.Tabs(this, (target) => {
        const index = Number(target.dataset.index);
        this.loadTabContent(index).then(() => {
          this.initSliderFor(index);
          const tabId = target.getAttribute("href");
          const slider = this.querySelector(tabId + " .swiper-container");
          const controlsContainer = this.querySelector(tabId + " .m-slider-controls");
          // trigger update slider
          slider && slider.swiper && slider.swiper.update();
          const firstItem = slider && (slider.querySelector(".m-image") || slider.querySelector(".m-placeholder-svg"));
          if (firstItem && controlsContainer) {
            const itemHeight = firstItem.clientHeight;
            controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");
          }
        });
      });
    }

    // Non-active tabs are server-rendered as an empty shell (see
    // product-tabs.liquid) to keep them out of the initial DOM. The first time
    // a tab is actually opened, pull its real cards in via the same Section
    // Rendering API the load-more button already uses, then swap them in.
    loadTabContent(index) {
      const wrapper = this.domNodes.tabContent[index];
      if (!wrapper || wrapper.dataset.loaded === "true") return Promise.resolve();

      this.tabLoadPromises = this.tabLoadPromises || {};
      if (this.tabLoadPromises[index]) return this.tabLoadPromises[index];

      const tabId = wrapper.getAttribute("id");
      const productsContainer = wrapper.querySelector(this.selectors.productsContainer);

      this.tabLoadPromises[index] = fetchSection(this.id, { url: wrapper.dataset.url, fromCache: true })
        .then((dom) => {
          const freshProducts = dom.querySelector(`#${tabId} ${this.selectors.productsContainer}`);
          if (freshProducts && productsContainer) {
            productsContainer.innerHTML = freshProducts.innerHTML;
          }
          wrapper.dataset.loaded = "true";
        })
        .catch((err) => console.error(`Failed to load tab ${index}:`, err));

      return this.tabLoadPromises[index];
    }

    // Fetching a tab only when it's clicked means the shopper sees the empty
    // skeleton for a beat on every first visit to that tab. Filling the rest
    // in during idle time - after the page has already painted - removes that
    // flash without costing anything on initial load.
    prefetchOtherTabs() {
      const schedule = window.requestIdleCallback || ((cb) => setTimeout(cb, 1500));
      schedule(() => {
        this.domNodes.tabContent.forEach((_, index) => this.loadTabContent(index));
      });
    }

    initSliderByScreenSize(sliderContainer) {
      const mobileDisableSlider = this.dataset.mobileDisableSlider === "true";
      const slider = sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = sliderContainer.querySelector(".m-slider-controls");

      if (MinimogTheme.config.mqlMobile && mobileDisableSlider) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        slider.classList.remove("swiper-container");
        if (slider.swiper) slider.swiper.destroy(false, true);
      } else {
        controlsContainer && controlsContainer.classList.remove("m:hidden");
        setTimeout(() => {
          this.initSlider(sliderContainer);
        });
      }
    }

    initSlider(sliderContainer) {
      const layoutWrapper = sliderContainer.querySelector(".m-product-list");
      const swiper = sliderContainer && sliderContainer.querySelector(".m-mixed-layout__wrapper");
      const controlsContainer = sliderContainer.querySelector(".m-slider-controls");
      const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
      const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
      const slideItemsLength =
        sliderContainer &&
        sliderContainer.querySelector(".swiper-wrapper") &&
        sliderContainer.querySelector(".swiper-wrapper").childElementCount;
      if (parseInt(this.items) >= slideItemsLength) {
        controlsContainer && controlsContainer.classList.add("m:hidden");
        layoutWrapper.classList.add("m-mixed-layout--mobile-grid");
        return;
      }

      swiper && swiper.classList.add("swiper-container");

      let slider = new MinimogLibs.Swiper(swiper, {
        slidesPerView: 2,
        showPagination: this.showPagination,
        showNavigation: this.showNavigation,
        loop: this.enableFlashsale ? false : true,
        pagination: this.showPagination
          ? {
              el: sliderContainer.querySelector(".swiper-pagination"),
              clickable: true,
            }
          : false,
        breakpoints: {
          768: {
            slidesPerView: parseInt(this.items) >= 3 ? 3 : parseInt(this.items),
          },
          992: {
            slidesPerView: parseInt(this.items) >= 4 ? 4 : parseInt(this.items),
          },
          1280: {
            slidesPerView: parseInt(this.items),
          },
        },
        threshold: 2,
        on: {
          init: function () {
            setTimeout(() => {
              // Calculate controls position
              const firstItem =
                sliderContainer.querySelector(".m-image") || sliderContainer.querySelector(".m-placeholder-svg");
              if (firstItem && controlsContainer) {
                const itemHeight = firstItem.clientHeight;
                controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

                prevButton.classList.remove("m:hidden");
                nextButton.classList.remove("m:hidden");
              }
            }, 200);
          },
          breakpoint: (swiper, breakpointParams) => {
            if (controlsContainer) {
              const { slidesPerView } = breakpointParams;
              if (slideItemsLength > slidesPerView) {
                controlsContainer.classList.remove("m:hidden");
                swiper.allowTouchMove = true;
              } else {
                controlsContainer.classList.add("m:hidden");
                swiper.allowTouchMove = false;
              }
            }
          },
        },
      });

      if (slider && this.showNavigation) {
        prevButton && prevButton.addEventListener("click", () => slider.slidePrev());
        nextButton && nextButton.addEventListener("click", () => slider.slideNext());
      }
    }

    initMobileSelect() {
      this.select = this.querySelector("[data-tab-select]");
      this.select.addEventListener("change", () => {
        const index = parseInt(this.select.value);
        this.tabs.setActiveTab(index);
        this.loadTabContent(index).then(() => {
          this.initSliderFor(index);
          const slider = this.tabs && this.tabs.currentTab && this.tabs.currentTab.querySelector(".swiper-container");
          const controlsContainer =
            this.tabs && this.tabs.currentTab && this.tabs.currentTab.querySelector(".m-slider-controls");
          const prevButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-prev");
          const nextButton = controlsContainer && controlsContainer.querySelector(".m-slider-controls__button-next");
          slider && slider.swiper && slider.swiper.update();
          const firstItem = slider && slider.querySelector(".m-image");
          if (firstItem && controlsContainer) {
            const itemHeight = firstItem.clientHeight;
            controlsContainer.style.setProperty("--offset-top", parseInt(itemHeight) / 2 + "px");

            prevButton.classList.remove("m:hidden");
            nextButton.classList.remove("m:hidden");
          }
        });
      });
    }

    initLoadMore(wrapper) {
      addEventDelegate({
        context: wrapper,
        selector: this.selectors.loadMoreBtn,
        handler: (e) => {
          e.preventDefault();
          this.handleLoadMore(wrapper);
        },
      });
    }

    handleLoadMore(wrapper) {
      const loadBtn = wrapper.querySelector(this.selectors.loadMoreBtn);
      const productsContainer = wrapper.querySelector(this.selectors.productsContainer);

      let currentPage = wrapper.dataset.page;
      currentPage = parseInt(currentPage);
      const totalPages = wrapper.dataset.totalPages;
      this.toggleLoading(loadBtn, true);

      const url = wrapper.dataset.url;
      const dataUrl = `${url}?page=${currentPage + 1}&section_id=${this.id}`;
      fetchCache(dataUrl).then((html) => {
        currentPage++;
        wrapper.dataset.page = currentPage;
        this.toggleLoading(loadBtn, false);
        const dom = generateDomFromString(html);
        const tabId = wrapper.getAttribute("id");
        const products = dom.querySelector(`#${tabId} ${this.selectors.productsContainer}`);

        if (products) {
          Array.from(products.childNodes).forEach((product) => productsContainer.appendChild(product));
        }

        if (currentPage >= parseInt(totalPages)) loadBtn && loadBtn.remove();
      });

      // Remove button focus
      loadBtn.blur();
    }

    toggleLoading(loadBtn, status) {
      if (!loadBtn) return;
      if (status) {
        loadBtn.classList.add("m-spinner-loading");
      } else {
        loadBtn.classList.remove("m-spinner-loading");
      }
    }
  }
  customElements.define("m-product-tabs", MProductTabs);
}
